import { createInputRouter } from "./input-router.js";
import type { JourneyInputAction } from "./input-router.js";
import type { ReadPosition } from "./controller.js";
import type { JourneyPresentation } from "./preferences.js";

export interface JourneyShellOptions {
  readonly root: Document;
  readonly emit: (action: JourneyInputAction) => void;
  readonly presentation?: () => JourneyPresentation;
  readonly itemId?: () => string | null;
  readonly applyReadPosition?: (position: ReadPosition) => void;
  readonly onRestoreError?: (error: unknown) => void;
}

export interface JourneyShell {
  captureReadPosition(): ReadPosition;
  restoreReadPosition(position: ReadPosition): void;
  destroy(): void;
}

type UnavailableJourneyChoice = Readonly<{
  available: false;
  reason: string;
  href?: never;
  action?: never;
}>;

export type JourneyNavigationChoice =
  | Readonly<{ available: true; href: string; reason?: never; action?: never }>
  | UnavailableJourneyChoice;

export type JourneyActionChoice =
  | Readonly<{ available: true; href?: never; reason?: never; action?: never }>
  | UnavailableJourneyChoice;

export interface JourneyChoiceModel {
  readonly read: JourneyNavigationChoice;
  readonly write: JourneyNavigationChoice;
  readonly remix: JourneyActionChoice;
  readonly wander: JourneyActionChoice;
}

interface JourneyHistoryState {
  readonly version: 1;
  readonly entryId: string;
  readonly parentEntryId?: string;
  readonly readPosition?: ReadPosition;
}

interface PointerOwner {
  readonly anchor: HTMLAnchorElement;
  readonly href: string;
  readonly pointerId: number;
  captured: boolean;
}

interface ScrollOwnership {
  readonly original: ScrollRestoration;
  readonly owners: Set<symbol>;
}

interface NativeNorthStarEntrance {
  readonly version: 1;
  readonly sourceRoute: string;
  readonly targetRoute: "/app/north-star.html";
  readonly sourceEntryNonce: string;
  readonly navigationNonce: string;
  readonly sourceHistoryLength: number;
  readonly createdAt: number;
}

const BUTTON_ACTIONS = new Set<JourneyInputAction["type"]>([
  "SAVE",
  "REMIX",
  "REVEAL_GUIDANCE",
]);
const WHEEL_LINE_PIXELS = 16;
const JOURNEY_HISTORY_KEY = "__undefHaikuJourneyV1";
const JOURNEY_HISTORY_VERSION = 1;
const MISSING_HISTORY_VALUE = Symbol("missing-history-value");
const UNREADABLE_HISTORY_VALUE = Symbol("unreadable-history-value");
const INVALID_HISTORY_VALUE = Symbol("invalid-history-value");
const UNSAFE_HISTORY_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const NATIVE_NORTH_STAR_TARGET_PATH = "/app/north-star.html";
const NATIVE_NORTH_STAR_SOURCE_PATHS = new Set(["/app/index.html", "/app/compose.html", "/app/haiku.html"]);
const NATIVE_NORTH_STAR_ENTRANCE_KEY = "__undefHaikuNorthStarEntranceV1";
const NATIVE_NORTH_STAR_ENTRANCE_FRESHNESS_MS = 30_000;
const JOURNEY_CHOICES = [
  ["read", "Read"],
  ["write", "Write"],
  ["remix", "Remix"],
  ["wander", "Wander"],
] as const;
const scrollOwnership = new WeakMap<History, ScrollOwnership>();
let nextHistoryEntryId = 0;

type TimingGetter = (this: object) => unknown;
type TimingEntriesMethod = (this: object, type: string) => unknown;

function readTimingGetter(prototype: object, key: PropertyKey): TimingGetter | null {
  const accessor = Object.getOwnPropertyDescriptor(prototype, key);
  const getterValue: unknown =
    accessor === undefined ? undefined : Object.getOwnPropertyDescriptor(accessor, "get")?.value;
  return typeof getterValue === "function" ? (getterValue as TimingGetter) : null;
}

// This protects mutations after module evaluation. Code that runs before this
// bootstrap has arbitrary same-origin control and is outside this boundary.
const nativeNorthStarTimingIntrinsics = (() => {
  try {
    if (typeof PerformanceNavigationTiming !== "function" || typeof PerformanceEntry !== "function") return null;
    const navigationTiming = PerformanceNavigationTiming;
    const performanceEntry = PerformanceEntry;
    const navigationPrototype = navigationTiming.prototype;
    const performanceEntryPrototype = performanceEntry.prototype;
    if (
      navigationPrototype === null ||
      typeof navigationPrototype !== "object" ||
      performanceEntryPrototype === null ||
      typeof performanceEntryPrototype !== "object"
    ) {
      return null;
    }
    const typeGetter = readTimingGetter(navigationPrototype, "type");
    const entryTypeGetter = readTimingGetter(performanceEntryPrototype, "entryType");
    const performanceInstance = performance;
    const getEntriesByTypeValue: unknown = Reflect.get(performanceInstance, "getEntriesByType");
    if (typeGetter === null || entryTypeGetter === null || typeof getEntriesByTypeValue !== "function") return null;
    return Object.freeze({
      navigationTiming,
      performanceEntry,
      navigationPrototype,
      performanceEntryPrototype,
      typeGetter,
      entryTypeGetter,
      performanceInstance,
      getEntriesByType: getEntriesByTypeValue as TimingEntriesMethod,
    });
  } catch {
    return null;
  }
})();

export function isCapturedNativeNorthStarNavigateEntries(entries: unknown): boolean {
  const intrinsics = nativeNorthStarTimingIntrinsics;
  if (intrinsics === null || !Array.isArray(entries) || entries.length !== 1) return false;
  const entry: unknown = entries[0];
  if (
    entry === null ||
    typeof entry !== "object" ||
    !(entry instanceof intrinsics.navigationTiming) ||
    Object.getPrototypeOf(entry) !== intrinsics.navigationPrototype
  ) {
    return false;
  }
  try {
    return (
      Reflect.apply(intrinsics.entryTypeGetter, entry, []) === "navigation" &&
      Reflect.apply(intrinsics.typeGetter, entry, []) === "navigate"
    );
  } catch {
    return false;
  }
}

function newHistoryEntryId(): string {
  nextHistoryEntryId += 1;
  return `journey-${nextHistoryEntryId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  try {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  } catch {
    return false;
  }
}

function ownDataValue(record: Record<string, unknown>, key: string): unknown {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(record, key);
    if (descriptor === undefined) return MISSING_HISTORY_VALUE;
    return "value" in descriptor ? descriptor.value : UNREADABLE_HISTORY_VALUE;
  } catch {
    return UNREADABLE_HISTORY_VALUE;
  }
}

function readHistoryState(value: unknown): JourneyHistoryState | null {
  if (!isRecord(value)) return null;
  const envelope = ownDataValue(value, JOURNEY_HISTORY_KEY);
  if (envelope === MISSING_HISTORY_VALUE || envelope === UNREADABLE_HISTORY_VALUE || !isRecord(envelope)) return null;
  if (ownDataValue(envelope, "version") !== JOURNEY_HISTORY_VERSION) return null;
  const entryId = ownDataValue(envelope, "entryId");
  const parentEntryId = ownDataValue(envelope, "parentEntryId");
  const positionValue = ownDataValue(envelope, "readPosition");
  if (entryId === MISSING_HISTORY_VALUE || entryId === UNREADABLE_HISTORY_VALUE
    || parentEntryId === UNREADABLE_HISTORY_VALUE || positionValue === UNREADABLE_HISTORY_VALUE
    || typeof entryId !== "string" || entryId === ""
    || (parentEntryId !== MISSING_HISTORY_VALUE && (typeof parentEntryId !== "string" || parentEntryId === ""))) return null;
  if (positionValue === MISSING_HISTORY_VALUE) {
    return Object.freeze({
      version: JOURNEY_HISTORY_VERSION,
      entryId,
      ...(parentEntryId === MISSING_HISTORY_VALUE ? {} : { parentEntryId }),
    });
  }
  const position = readPosition(positionValue);
  if (position === null) return null;
  return Object.freeze({
    version: JOURNEY_HISTORY_VERSION,
    entryId,
    ...(parentEntryId === MISSING_HISTORY_VALUE ? {} : { parentEntryId }),
    readPosition: position,
  });
}

function readPosition(value: unknown): ReadPosition | null {
  if (!isRecord(value)) return null;
  const route = ownDataValue(value, "route");
  const presentation = ownDataValue(value, "presentation");
  const itemId = ownDataValue(value, "itemId");
  const scrollX = ownDataValue(value, "scrollX");
  const scrollY = ownDataValue(value, "scrollY");
  const focusId = ownDataValue(value, "focusId");
  if (typeof route !== "string"
    || (presentation !== "ordered" && presentation !== "settling")
    || (itemId !== null && typeof itemId !== "string")
    || typeof scrollX !== "number" || !Number.isFinite(scrollX)
    || typeof scrollY !== "number" || !Number.isFinite(scrollY)
    || (focusId !== null && typeof focusId !== "string")) return null;
  return Object.freeze({
    route,
    presentation,
    itemId,
    scrollX,
    scrollY,
    focusId,
  });
}

function snapshotExtensibleHistoryValue(
  value: unknown,
  visiting: WeakSet<object>,
  snapshots: WeakMap<object, Record<string, unknown>>,
): unknown {
  if (value === null) return null;
  if (typeof value !== "object") {
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
      || typeof value === "undefined" || typeof value === "bigint" ? value : INVALID_HISTORY_VALUE;
  }
  if (!isRecord(value) || visiting.has(value)) return INVALID_HISTORY_VALUE;
  const existing = snapshots.get(value);
  if (existing !== undefined) return existing;
  try {
    const prototype = Reflect.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return INVALID_HISTORY_VALUE;
    const snapshot = Object.create(prototype) as Record<string, unknown>;
    visiting.add(value);
    for (const key of Reflect.ownKeys(value)) {
      if (typeof key !== "string" || UNSAFE_HISTORY_KEYS.has(key)) return INVALID_HISTORY_VALUE;
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (descriptor === undefined || !("value" in descriptor) || !descriptor.enumerable
        || !descriptor.writable || !descriptor.configurable) return INVALID_HISTORY_VALUE;
      const nestedSnapshot = snapshotExtensibleHistoryValue(descriptor.value, visiting, snapshots);
      if (nestedSnapshot === INVALID_HISTORY_VALUE) return INVALID_HISTORY_VALUE;
      Object.defineProperty(snapshot, key, {
        value: nestedSnapshot,
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    visiting.delete(value);
    snapshots.set(value, snapshot);
    return snapshot;
  } catch {
    return INVALID_HISTORY_VALUE;
  }
}

function copyExtensibleHistoryState(rawState: unknown): Record<string, unknown> | null {
  if (rawState === null) return {};
  const snapshot = snapshotExtensibleHistoryValue(rawState, new WeakSet(), new WeakMap());
  if (snapshot === INVALID_HISTORY_VALUE || !isRecord(snapshot)) return null;
  try {
    if (typeof structuredClone !== "function") return null;
    void structuredClone(snapshot);
    return snapshot;
  } catch {
    return null;
  }
}

function isExactNativeNorthStarTarget(view: Window): boolean {
  return view.location.pathname === NATIVE_NORTH_STAR_TARGET_PATH
    && view.location.search === "" && view.location.hash === "";
}

function nativeNorthStarReferrerRoute(root: Document, view: Window): string | null {
  try {
    const referrer = new URL(root.referrer);
    return referrer.origin === view.location.origin && referrer.search === "" && referrer.hash === ""
      && NATIVE_NORTH_STAR_SOURCE_PATHS.has(referrer.pathname) ? referrer.pathname : null;
  } catch {
    return null;
  }
}

function isNativeNorthStarNonce(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
}

function readNativeNorthStarEntrance(value: unknown): NativeNorthStarEntrance | null {
  if (!isRecord(value)
    || ownDataValue(value, "version") !== 1
    || ownDataValue(value, "targetRoute") !== NATIVE_NORTH_STAR_TARGET_PATH) return null;
  const sourceRoute = ownDataValue(value, "sourceRoute");
  const sourceEntryNonce = ownDataValue(value, "sourceEntryNonce");
  const navigationNonce = ownDataValue(value, "navigationNonce");
  const sourceHistoryLength = ownDataValue(value, "sourceHistoryLength");
  const createdAt = ownDataValue(value, "createdAt");
  if (typeof sourceRoute !== "string" || !NATIVE_NORTH_STAR_SOURCE_PATHS.has(sourceRoute)
    || !isNativeNorthStarNonce(sourceEntryNonce) || !isNativeNorthStarNonce(navigationNonce)
    || typeof sourceHistoryLength !== "number" || !Number.isSafeInteger(sourceHistoryLength) || sourceHistoryLength < 1
    || typeof createdAt !== "number" || !Number.isFinite(createdAt)) return null;
  return Object.freeze({
    version: 1,
    sourceRoute,
    targetRoute: NATIVE_NORTH_STAR_TARGET_PATH,
    sourceEntryNonce,
    navigationNonce,
    sourceHistoryLength,
    createdAt,
  });
}

interface NativeNorthStarEntranceConsumption {
  readonly entrance: NativeNorthStarEntrance | null;
  readonly needsTombstone: boolean;
}

interface ActiveJourneyShell {
  destroy(): void;
}

const activeJourneyShells = new WeakMap<Window, ActiveJourneyShell>();
const rejectedNativeNorthStarEntranceViews = new WeakSet<Window>();
const NATIVE_NORTH_STAR_ENTRANCE_REJECTED_KEY = "__undefHaikuNorthStarEntranceRejectedV1";

function noNativeNorthStarEntrance(): NativeNorthStarEntranceConsumption {
  return { entrance: null, needsTombstone: false };
}

function consumeNativeNorthStarEntrance(view: Window): NativeNorthStarEntranceConsumption {
  if (rejectedNativeNorthStarEntranceViews.has(view)) return noNativeNorthStarEntrance();
  try {
    const storage = view.sessionStorage;
    const raw = storage.getItem(NATIVE_NORTH_STAR_ENTRANCE_KEY);
    if (raw === null) return noNativeNorthStarEntrance();
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      try {
        storage.removeItem(NATIVE_NORTH_STAR_ENTRANCE_KEY);
      } catch {
        // A malformed record has no authority even when it cannot be removed.
      }
      return noNativeNorthStarEntrance();
    }
    const entrance = readNativeNorthStarEntrance(parsed);
    try {
      storage.removeItem(NATIVE_NORTH_STAR_ENTRANCE_KEY);
    } catch {
      if (entrance !== null) {
        rejectedNativeNorthStarEntranceViews.add(view);
        return { entrance: null, needsTombstone: true };
      }
      return noNativeNorthStarEntrance();
    }
    return { entrance, needsTombstone: false };
  } catch {
    return noNativeNorthStarEntrance();
  }
}

function writeRejectedNativeNorthStarEntranceTombstone(view: Window): void {
  try {
    const tombstone: Record<string, unknown> = {};
    Object.defineProperty(tombstone, NATIVE_NORTH_STAR_ENTRANCE_REJECTED_KEY, {
      value: Object.freeze({ version: 1 }),
      enumerable: true,
      configurable: true,
      writable: true,
    });
    view.history.replaceState(tombstone, "", view.location.href);
  } catch {
    // The per-window rejection latch still prevents a same-document remint.
  }
}

function writeHistoryState(snapshot: Record<string, unknown>, state: JourneyHistoryState): Record<string, unknown> | null {
  try {
    Object.defineProperty(snapshot, JOURNEY_HISTORY_KEY, {
      value: state,
      enumerable: true,
      configurable: true,
      writable: true,
    });
    return snapshot;
  } catch {
    return null;
  }
}

function acquireScrollRestoration(history: History): symbol {
  const owner = Symbol("journey-scroll-owner");
  let ownership = scrollOwnership.get(history);
  if (ownership === undefined) {
    ownership = { original: history.scrollRestoration, owners: new Set() };
    scrollOwnership.set(history, ownership);
  }
  ownership.owners.add(owner);
  history.scrollRestoration = "manual";
  return owner;
}

function releaseScrollRestoration(history: History, owner: symbol): void {
  const ownership = scrollOwnership.get(history);
  if (ownership === undefined || !ownership.owners.delete(owner)) return;
  if (ownership.owners.size !== 0) return;
  history.scrollRestoration = ownership.original;
  scrollOwnership.delete(history);
}

function anchorFromTarget(target: EventTarget | null): HTMLAnchorElement | null {
  return target instanceof Element ? target.closest("a[href]") : null;
}

function isEditable(target: EventTarget | null): boolean {
  return target instanceof HTMLElement
    && (target.isContentEditable || target.matches("input, textarea, select"));
}

function requireWindow(root: Document): Window {
  const view = root.defaultView;
  if (view === null) throw new Error("Journey shell requires a document with a Window");
  return view;
}

export function renderJourneyChoices(
  container: HTMLElement,
  model: JourneyChoiceModel,
): void {
  const root = container.ownerDocument;
  const view = requireWindow(root);
  const fragment = root.createDocumentFragment();
  for (const [id, label] of JOURNEY_CHOICES) {
    const choice = model[id];
    let element: HTMLElement;
    if (!choice.available) {
      if (typeof choice.reason !== "string" || choice.reason.trim() === "") {
        throw new TypeError(`Unavailable Journey choice ${label} requires a reason`);
      }
      element = root.createElement("span");
      element.setAttribute("aria-disabled", "true");
      element.textContent = `${label}: ${choice.reason}`;
    } else if (id === "read" || id === "write") {
      if (typeof choice.href !== "string" || "action" in choice) {
        throw new TypeError(`Journey choice ${label} requires a navigation href`);
      }
      const destination = new URL(choice.href, view.location.href);
      const valid = (destination.protocol === "http:" || destination.protocol === "https:")
        && destination.protocol === view.location.protocol
        && destination.origin === view.location.origin;
      if (!valid) throw new TypeError(`Journey choice ${label} requires a same-origin href`);
      const anchor = root.createElement("a");
      anchor.setAttribute("href", choice.href);
      anchor.textContent = label;
      element = anchor;
    } else {
      if ("href" in choice || "action" in choice) {
        throw new TypeError(`Journey choice ${label} has a fixed action`);
      }
      const button = root.createElement("button");
      button.type = "button";
      button.dataset.journeyAction = id === "remix" ? "REMIX" : "ADVANCE";
      if (id === "wander") button.dataset.journeyDirection = "1";
      button.textContent = label;
      element = button;
    }
    element.dataset.journeyChoice = id;
    fragment.append(element);
  }
  container.replaceChildren(fragment);
}

export function createJourneyShell(options: JourneyShellOptions): JourneyShell {
  const { root, emit } = options;
  const view = requireWindow(root);
  activeJourneyShells.get(view)?.destroy();
  const scrollOwner = acquireScrollRestoration(view.history);
  let suppressedAnchor: HTMLAnchorElement | null = null;
  let suppressionTimer: number | null = null;
  let pointerOwner: PointerOwner | null = null;
  let handlingPopState = false;
  let settlingPopState = false;
  let pendingReturnEntryId: string | null = null;
  let terminalReturnLatched = false;
  let destroyed = false;

  function captureReadPosition(): ReadPosition {
    const active = root.activeElement;
    return Object.freeze({
      route: `${view.location.pathname}${view.location.search}${view.location.hash}`,
      presentation: options.presentation?.() ?? "ordered",
      itemId: options.itemId?.() ?? null,
      scrollX: view.scrollX,
      scrollY: view.scrollY,
      focusId: active instanceof HTMLElement && active.id !== "" ? active.id : null,
    });
  }

  function restoreReadPosition(position: ReadPosition): void {
    const validated = readPosition(position);
    if (validated === null) return;
    options.applyReadPosition?.(validated);
    const focusTarget = validated.focusId === null ? null : root.getElementById(validated.focusId);
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    else if (root.activeElement instanceof HTMLElement) root.activeElement.blur();
    view.scrollTo(validated.scrollX, validated.scrollY);
  }

  function requestReturn(): void {
    if (pendingReturnEntryId !== null) return;
    const snapshot = copyExtensibleHistoryState(view.history.state);
    const state = snapshot === null ? null : readHistoryState(snapshot);
    if (state?.parentEntryId === undefined) {
      if (terminalReturnLatched) return;
      terminalReturnLatched = true;
      try {
        emit({ type: "RETURN" });
      } catch (error) {
        terminalReturnLatched = false;
        throw error;
      }
      return;
    }
    terminalReturnLatched = false;
    pendingReturnEntryId = state.parentEntryId;
    try {
      emit({ type: "RETURN" });
      view.history.back();
    } catch (error) {
      if (pendingReturnEntryId === state.parentEntryId) {
        pendingReturnEntryId = null;
      }
      throw error;
    }
  }

  function dispatch(action: JourneyInputAction): boolean {
    if (action.type === "OPEN") {
      const snapshot = copyExtensibleHistoryState(view.history.state);
      if (snapshot === null) return false;
      terminalReturnLatched = false;
      const state = readHistoryState(snapshot);
      const entryId = state?.entryId ?? newHistoryEntryId();
      const replacement = writeHistoryState(snapshot, {
          version: JOURNEY_HISTORY_VERSION,
          entryId,
          ...(state?.parentEntryId === undefined ? {} : { parentEntryId: state.parentEntryId }),
          readPosition: captureReadPosition(),
        });
      if (replacement === null) return false;
      view.history.replaceState(
        replacement,
        "",
        view.location.href,
      );
      view.history.pushState(
        {
          [JOURNEY_HISTORY_KEY]: {
            version: JOURNEY_HISTORY_VERSION,
            entryId: newHistoryEntryId(),
            parentEntryId: entryId,
          },
        },
        "",
        action.href,
      );
      emit(action);
      return true;
    }
    if (action.type === "RETURN") {
      if (settlingPopState && !handlingPopState) return true;
      if (!handlingPopState) {
        requestReturn();
        return true;
      }
    }
    emit(action);
    return true;
  }

  const input = createInputRouter(dispatch);

  function eligibleAnchor(event: MouseEvent | KeyboardEvent, anchor: HTMLAnchorElement): URL | null {
    if (event.defaultPrevented || (event instanceof MouseEvent && event.button !== 0)
      || event.metaKey || event.ctrlKey
      || event.shiftKey || event.altKey || anchor.hasAttribute("download")
      || (anchor.target !== "" && anchor.target.toLowerCase() !== "_self")) return null;
    const destination = new URL(anchor.href, view.location.href);
    const supportedProtocol = destination.protocol === view.location.protocol
      && (destination.protocol === "http:" || destination.protocol === "https:");
    return supportedProtocol && destination.origin === view.location.origin ? destination : null;
  }

  function onClick(event: MouseEvent): void {
    const anchor = anchorFromTarget(event.target);
    if (anchor !== null) {
      const destination = eligibleAnchor(event, anchor);
      if (destination === null) return;
      if (suppressedAnchor === anchor) {
        event.preventDefault();
        if (suppressionTimer !== null) view.clearTimeout(suppressionTimer);
        suppressionTimer = null;
        suppressedAnchor = null;
        return;
      }
      if (dispatch({ type: "OPEN", href: destination.href })) event.preventDefault();
      return;
    }
    const button = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>("button[data-journey-action]")
      : null;
    const type = button?.dataset.journeyAction as JourneyInputAction["type"] | undefined;
    if (type === "ADVANCE" && button?.dataset.journeyDirection === "1") {
      dispatch({ type: "ADVANCE", direction: 1 });
      return;
    }
    if (type !== undefined && BUTTON_ACTIONS.has(type)) dispatch({ type } as JourneyInputAction);
  }

  function releasePointer(owner: PointerOwner): void {
    if (!owner.captured) return;
    try {
      owner.anchor.releasePointerCapture(owner.pointerId);
    } catch {
      // Capture may already have been released by the browser.
    }
    owner.captured = false;
  }

  function clearPointer(cancelInput = true): void {
    const owner = pointerOwner;
    if (owner === null) return;
    pointerOwner = null;
    if (cancelInput) input.pointerCancel(owner.pointerId);
    releasePointer(owner);
  }

  function armClickSuppression(anchor: HTMLAnchorElement): void {
    suppressedAnchor = anchor;
    if (suppressionTimer !== null) view.clearTimeout(suppressionTimer);
    suppressionTimer = view.setTimeout(() => {
      suppressedAnchor = null;
      suppressionTimer = null;
    }, 0);
  }

  function onPointerDown(event: PointerEvent): void {
    clearPointer();
    const anchor = anchorFromTarget(event.target);
    const destination = anchor === null ? null : eligibleAnchor(event, anchor);
    if (anchor === null || destination === null) return;
    if (copyExtensibleHistoryState(view.history.state) === null) return;
    pointerOwner = { anchor, href: destination.href, pointerId: event.pointerId, captured: false };
    input.pointerDown(event);
    try {
      anchor.setPointerCapture(event.pointerId);
      pointerOwner.captured = true;
    } catch {
      // Synthetic events and unsupported pointers cannot be captured.
    }
  }

  function onPointerMove(event: PointerEvent): void {
    if (pointerOwner?.pointerId === event.pointerId) input.pointerMove(event);
  }

  function onPointerUp(event: PointerEvent): void {
    const owner = pointerOwner;
    if (owner === null || owner.pointerId !== event.pointerId) return;
    const sameAnchor = anchorFromTarget(event.target) === owner.anchor;
    const eligible = sameAnchor && eligibleAnchor(event, owner.anchor) !== null;
    const result = eligible ? input.pointerUp(event, owner.href) : "CANCELLED";
    if (!eligible) input.pointerCancel(owner.pointerId);
    clearPointer(false);
    if (result === "OPENED") {
      event.preventDefault();
      armClickSuppression(owner.anchor);
    } else if (result === "CANCELLED" && eligible) {
      armClickSuppression(owner.anchor);
    }
  }

  function onPointerCancel(event: PointerEvent): void {
    if (pointerOwner?.pointerId === event.pointerId) clearPointer();
  }

  function onLostPointerCapture(event: PointerEvent): void {
    if (pointerOwner?.pointerId === event.pointerId) clearPointer();
  }

  function onWheel(event: WheelEvent): void {
    let deltaPixels = event.deltaY;
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) deltaPixels *= WHEEL_LINE_PIXELS;
    else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) deltaPixels *= view.innerHeight;
    if (input.wheel(deltaPixels)) event.preventDefault();
  }

  function onKeyDown(event: KeyboardEvent): void {
    const radioCandidate = event.target instanceof Element
      ? event.target.closest<HTMLElement>('[role="radio"]')
      : null;
    const nativeRadio = event.target instanceof HTMLInputElement && event.target.type === "radio";
    const radioIntent = radioCandidate === null && !nativeRadio ? null : input.radioIntent(event.key);
    if (nativeRadio && radioIntent !== null) return;
    if (radioCandidate !== null && !nativeRadio && radioIntent !== null) {
      const intent = radioIntent;
      event.preventDefault();
      const disabled = (radio: HTMLElement): boolean => radio.getAttribute("aria-disabled") === "true"
        || radio.matches(":disabled") || radio.closest("[inert]") !== null;
      if (intent.type === "ACTIVATE" && (event.repeat || disabled(radioCandidate))) return;
      const group = radioCandidate.closest('[role="radiogroup"]');
      const radios = Array.from(
        group?.querySelectorAll<HTMLElement>('[role="radio"]') ?? [radioCandidate],
      ).filter((radio) => !(radio instanceof HTMLInputElement && radio.type === "radio"));
      const enabled = radios.filter((radio) => !disabled(radio));
      if (enabled.length === 0) return;

      let target: HTMLElement | null = radioCandidate;
      if (intent.type === "BOUNDARY") {
        target = intent.edge === "FIRST" ? enabled[0] : enabled[enabled.length - 1];
      } else if (intent.type === "MOVE") {
        const start = radios.indexOf(radioCandidate);
        target = null;
        for (let offset = 1; offset <= radios.length; offset += 1) {
          const index = (start + intent.direction * offset + radios.length) % radios.length;
          if (!disabled(radios[index])) {
            target = radios[index];
            break;
          }
        }
      }
      if (target === null || disabled(target)) return;
      for (const radio of radios) {
        radio.setAttribute("aria-checked", radio === target ? "true" : "false");
        radio.tabIndex = radio === target ? 0 : -1;
      }
      if (intent.type === "ACTIVATE") target.click();
      target.focus({ preventScroll: true });
      return;
    }
    if (isEditable(event.target) && !nativeRadio && event.key !== "Escape") return;
    const anchor = anchorFromTarget(event.target);
    const destination = anchor === null ? null : eligibleAnchor(event, anchor);
    const href = destination?.href;
    if (event.key === "Enter" && href !== undefined) {
      if (dispatch({ type: "OPEN", href })) event.preventDefault();
      return;
    }
    if (input.key({ key: event.key, href, repeat: event.repeat })) event.preventDefault();
  }

  function onPopState(event: PopStateEvent): void {
    if (settlingPopState) return;
    settlingPopState = true;
    try {
      terminalReturnLatched = false;
      const snapshot = copyExtensibleHistoryState(event.state);
      const state = snapshot === null ? null : readHistoryState(snapshot);
      if (state === null) {
        pendingReturnEntryId = null;
        return;
      }
      let matchesShellTraversal = false;
      if (pendingReturnEntryId !== null) {
        matchesShellTraversal = state.entryId === pendingReturnEntryId;
        pendingReturnEntryId = null;
      }
      if (state.readPosition !== undefined) {
        try {
          restoreReadPosition(state.readPosition);
        } catch (error) {
          options.onRestoreError?.(error);
        }
      }
      if (matchesShellTraversal) return;
      handlingPopState = true;
      try {
        input.historyReturn();
      } finally {
        handlingPopState = false;
      }
    } finally {
      settlingPopState = false;
    }
  }

function isTopLevelNativeNorthStarEntranceContext(): boolean {
  try {
    return view.top === view.self && view.opener === null;
  } catch {
    return false;
  }
}

function isNativeNorthStarNavigateTiming(view: Window): boolean {
  try {
    const intrinsics = nativeNorthStarTimingIntrinsics;
    if (intrinsics === null || view.performance !== intrinsics.performanceInstance) return false;
    const performanceConstructors = view as Window & {
      readonly PerformanceNavigationTiming?: unknown;
      readonly PerformanceEntry?: unknown;
    };
    const currentNavigationTiming = performanceConstructors.PerformanceNavigationTiming;
    const currentPerformanceEntry = performanceConstructors.PerformanceEntry;
    if (
      currentNavigationTiming !== intrinsics.navigationTiming ||
      currentPerformanceEntry !== intrinsics.performanceEntry ||
      Reflect.get(currentNavigationTiming, "prototype") !== intrinsics.navigationPrototype ||
      Reflect.get(currentPerformanceEntry, "prototype") !== intrinsics.performanceEntryPrototype ||
      readTimingGetter(intrinsics.navigationPrototype, "type") !== intrinsics.typeGetter ||
      readTimingGetter(intrinsics.performanceEntryPrototype, "entryType") !== intrinsics.entryTypeGetter ||
      Reflect.get(intrinsics.performanceInstance, "getEntriesByType") !== intrinsics.getEntriesByType
    ) {
      return false;
    }
    const entries = Reflect.apply(intrinsics.getEntriesByType, intrinsics.performanceInstance, ["navigation"]);
    return isCapturedNativeNorthStarNavigateEntries(entries);
  } catch {
    return false;
  }
}

function initializeNativeNorthStarReturnAuthority(): void {
    if (view.location.pathname !== NATIVE_NORTH_STAR_TARGET_PATH) return;
    const consumption = consumeNativeNorthStarEntrance(view);
    const entrance = consumption.entrance;
    const rawState: unknown = view.history.state;
    if (consumption.needsTombstone && rawState === null && isExactNativeNorthStarTarget(view)) {
      writeRejectedNativeNorthStarEntranceTombstone(view);
      return;
    }
    const existing = rawState === null ? null : copyExtensibleHistoryState(rawState);
    if (existing !== null && readHistoryState(existing) !== null) return;
    const referrerRoute = nativeNorthStarReferrerRoute(root, view);
  if (!isExactNativeNorthStarTarget(view) || !isTopLevelNativeNorthStarEntranceContext()
      || !isNativeNorthStarNavigateTiming(view)
      || referrerRoute === null || rawState !== null || entrance === null
      || entrance.sourceRoute !== referrerRoute) return;
    const age = Date.now() - entrance.createdAt;
    if (age < 0 || age > NATIVE_NORTH_STAR_ENTRANCE_FRESHNESS_MS
      || entrance.sourceHistoryLength + 1 !== view.history.length) return;
    const replacement = writeHistoryState({}, {
      version: JOURNEY_HISTORY_VERSION,
      entryId: entrance.navigationNonce,
      parentEntryId: entrance.sourceEntryNonce,
    });
    if (replacement === null) return;
    view.history.replaceState(replacement, "", view.location.href);
  }

  initializeNativeNorthStarReturnAuthority();

  root.addEventListener("click", onClick);
  root.addEventListener("pointerdown", onPointerDown);
  root.addEventListener("pointermove", onPointerMove);
  root.addEventListener("pointerup", onPointerUp);
  root.addEventListener("pointercancel", onPointerCancel);
  root.addEventListener("lostpointercapture", onLostPointerCapture);
  root.addEventListener("wheel", onWheel, { passive: false });
  root.addEventListener("keydown", onKeyDown);
  view.addEventListener("popstate", onPopState);

  const shell = {
    captureReadPosition,
    restoreReadPosition,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      clearPointer();
      suppressedAnchor = null;
      if (suppressionTimer !== null) view.clearTimeout(suppressionTimer);
      suppressionTimer = null;
      pendingReturnEntryId = null;
      terminalReturnLatched = false;
      settlingPopState = false;
      root.removeEventListener("click", onClick);
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerup", onPointerUp);
      root.removeEventListener("pointercancel", onPointerCancel);
      root.removeEventListener("lostpointercapture", onLostPointerCapture);
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("keydown", onKeyDown);
      view.removeEventListener("popstate", onPopState);
      releaseScrollRestoration(view.history, scrollOwner);
      if (activeJourneyShells.get(view) === shell) activeJourneyShells.delete(view);
    },
  };
  activeJourneyShells.set(view, shell);
  return shell;
}
